import * as React from 'react';

import Mousetrap from 'mousetrap';

import type {User} from 'nutshell-core/types';

import {useGetSessionUser} from '../../core/use-get-session-user';

import {AddNewButton} from './add-new/add-new-button';
import {AddNewPopover} from './add-new/add-new-popover';
import {CommandPaletteModal} from './command-palette-modal/command-palette-modal';
import {NavbarRightSection} from './navbar-right-section';
import {SearchBarContainer} from './search-bar/search-results/search-bar-container';

type Props = {
    userInitials: string;
    user: User | null | undefined;
};

export function Navbar(props: Props) {
    const [isCreateNewActive, setIsCreateNewActive] = React.useState(false);
    const [isCommandPaletteOpen, setIsCommandPaletteOpen] = React.useState(false);
    const addNewRef = React.useRef(null);

    const {user} = useGetSessionUser();

    const isAiAgentSidebarOpen = Boolean(user && user.isAiAgentSidebarOpen);
    const isMainSidebarCollapsed = Boolean(user && user.isMainSidebarCollapsed);

    React.useEffect(() => {
        if (props.user && props.user.hasDisabledShortcuts) {
            return;
        }

        Mousetrap.bind('g a', (e) => {
            e.preventDefault();
            setIsCreateNewActive(!isCreateNewActive);
        });

        return () => {
            Mousetrap.unbind('g a');
        };
    }, [isCreateNewActive, props.user]);

    React.useEffect(() => {
        if (!NutClientConfig.hasCommandPaletteFF) {
            return;
        }

        if (props.user && props.user.hasDisabledShortcuts) {
            return;
        }

        Mousetrap.bind(['meta+k', 'ctrl+k'], (e) => {
            e.preventDefault();
            setIsCommandPaletteOpen((prev) => !prev);
        });

        return () => {
            Mousetrap.unbind(['meta+k', 'ctrl+k']);
        };
    }, [props.user]);

    return (
        <>
            <div
                className='nut-navbar--container'
                data-is-ai-agent-sidebar-open={isAiAgentSidebarOpen}
                data-is-main-sidebar-collapsed={isMainSidebarCollapsed}
            >
                <div className='nut-navbar--left-section'>
                    <SearchBarContainer
                        user={props.user}
                        onOpenCommandPalette={
                            NutClientConfig.hasCommandPaletteFF
                                ? () => setIsCommandPaletteOpen(true)
                                : undefined
                        }
                    />
                    <AddNewButton
                        isActive={isCreateNewActive}
                        onClick={() => setIsCreateNewActive(!isCreateNewActive)}
                        buttonRef={addNewRef}
                    />
                </div>
                <NavbarRightSection user={props.user} userInitials={props.userInitials} />
            </div>
            {isCreateNewActive && (
                <AddNewPopover
                    addNewRef={addNewRef}
                    setIsCreateNewActive={setIsCreateNewActive}
                    user={props.user}
                />
            )}
            {NutClientConfig.hasCommandPaletteFF && (
                <CommandPaletteModal
                    isOpen={isCommandPaletteOpen}
                    onClose={() => setIsCommandPaletteOpen(false)}
                />
            )}
        </>
    );
}
